import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from "../middleware/AuthContext";
import {
  fetchDistributionLists,
  fetchEmails,
  addEmail,
  updateEmail,
  removeEmail
} from '../apis/fetchDistributionListApi';
import '../styles/global.css'


const DistributionListApp: React.FC = () => {
  const { csrfToken } = useAuth(); 
  const [lists, setLists] = useState<string[]>([]);
  const [selectedList, setSelectedList] = useState<{ name: string; emails: string[] } | null>(null);
  const [filteredEmails, setFilteredEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailToEdit, setEmailToEdit] = useState<string | null>(null); 
  const [editedEmail, setEditedEmail] = useState('');
  const [originalEmailForEdit, setOriginalEmailForEdit] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const lists = (await fetchDistributionLists()).sort();
        setLists(lists);
      } catch (error: string | unknown) {
        console.error("Error fetching distribution lists:", error);
        setErrorMessage(
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        );
      }
    };

    fetchLists();
  }, []);


  useEffect(() => {
    if (selectedList) {
      if (newEmail.trim()) {
        setFilteredEmails(
          selectedList.emails.filter(email =>
            email.toLowerCase().includes(newEmail.toLowerCase())
          )
        );
      } else {
        setFilteredEmails(selectedList.emails);
      }
    }
  }, [newEmail, selectedList]);


  const handleSelectList = async (listName: string) => {
    try {
      setErrorMessage(null);
      setEmailToEdit(null);
      setEditedEmail('');
      setOriginalEmailForEdit(null);
      setFilteredEmails([]);


      const emails = (await fetchEmails(listName)).sort();
      setSelectedList(null);
      setSelectedList({ name: listName, emails });
    } catch (error: unknown) {
      setErrorMessage(
        typeof error === 'object' && error !== null && 'message' in error
          ? (error as { message: string }).message
          : String(error)
      );
    }
  };

  const handleAddEmail = async () => {
    if (selectedList && newEmail.trim()) {
      try {
        const emailsToAdd = newEmail.split(';').map((email) => email.trim()).filter(Boolean).join(';');

      await addEmail(selectedList.name, emailsToAdd, csrfToken);
        setSelectedList({
          ...selectedList,
          emails: [...new Set([...selectedList.emails, ...emailsToAdd.split(';')])].sort(),
        });
        setErrorMessage(null);
      } catch (error: unknown) {
        setErrorMessage(
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        );
      }
    }
  };

  const handleDeleteClick = (email: string) => {
    setEmailToDelete(email);
    setShowModal(true);
  };
  const handleCancelDelete = () => {
    setShowModal(false);
    setEmailToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (emailToDelete && selectedList) {
      try {
        await removeEmail(selectedList.name, emailToDelete, csrfToken); 
        setSelectedList({
          ...selectedList,
          emails: selectedList.emails.filter((e) => e !== emailToDelete),
        });
        setErrorMessage(null);
      } catch (error: unknown) {
        setErrorMessage(
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        );
      }
    }
    setShowModal(false);
    setEmailToDelete(null);
  };


  const handleEditEmailStart = (email: string) => {
    setOriginalEmailForEdit(email); 
    setEmailToEdit(email); 
    setEditedEmail(email); 
  };


  const handleCancelEdit = () => {
    setEmailToEdit(null); 
    setOriginalEmailForEdit(null); 
  };

  const handleConfirmEdit = async () => {
    if (selectedList && editedEmail !== originalEmailForEdit && originalEmailForEdit !== null) {
      try {
        await updateEmail(selectedList.name, originalEmailForEdit, editedEmail, csrfToken);
        setSelectedList({
          ...selectedList,
          emails: selectedList.emails
            .map(e => (e === originalEmailForEdit ? editedEmail : e))
            .sort(),
        });
        setEmailToEdit(null);
        setOriginalEmailForEdit(null);
        setErrorMessage(null);
      } catch (error: unknown) {
        setErrorMessage(
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        );
      }
    }
  };



  const exportToCSV = () => {
    if (!selectedList) return;
    const csvContent = `data:text/csv;charset=utf-8,${selectedList.name+'_emails \n'+selectedList.emails.join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${selectedList.name}_distribution_list.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedList || !event.target.files || event.target.files.length === 0) return;
    
    const file = event.target.files[0];
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        setImportMessage(null);
        setErrorMessage(null);
        
        const text = e.target?.result as string;
        const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
        
        // Skip the first line if it does not contain an email address
        const importedEmailsRaw = lines.filter((line, index) => {
          if (index === 0 && !line.includes('@')) {
            return false;
          }
          return line.includes('@');
        });
        // Normalize imported emails to lowercase for consistent matching
        const importedEmails = importedEmailsRaw.map(email => email.toLowerCase());
        
        const currentEmails = selectedList.emails;
        // Build a normalized set for current emails and a map from normalized -> original
        const currentNormalizedSet = new Set(currentEmails.map(e => e.toLowerCase()));
        const emailsToAdd = importedEmails.filter(normEmail => !currentNormalizedSet.has(normEmail));
        const emailsToRemove = currentEmails.filter(originalEmail => !importedEmails.includes(originalEmail.toLowerCase()));
        
        let added = 0;
        let removed = 0;
        const errors: string[] = [];
        
        // Add new emails
        for (const email of emailsToAdd) {
          try {
            await addEmail(selectedList.name, email, csrfToken);
            added++;
          } catch (error) {
            errors.push(`Failed to add ${email}: ${error}`);
          }
        }
        
        // Remove emails not in CSV
        for (const email of emailsToRemove) {
          try {
            await removeEmail(selectedList.name, email, csrfToken);
            removed++;
          } catch (error) {
            errors.push(`Failed to remove ${email}: ${error}`);
          }
        }
        
        // Refresh the list
        const updatedEmails = (await fetchEmails(selectedList.name)).sort();
        setSelectedList({ name: selectedList.name, emails: updatedEmails });
        
        // Set success message
        let message = `Import complete: ${added} email(s) added, ${removed} email(s) removed (emails normalized to lowercase)`;
        if (errors.length > 0) {
          message += `. Errors: ${errors.join('; ')}`;
        }
        setImportMessage(message);
        
      } catch (error) {
        setErrorMessage(
          typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : String(error)
        );
      }
    };
    
    reader.readAsText(file);
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

return (
    <MainLayout>
      {errorMessage && (
        <div className="error-message">{errorMessage}</div>
      )}
      {importMessage && (
        <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px', marginBottom: '10px', borderRadius: '5px' }}>
          {importMessage}
        </div>
      )}
      <div className="container">
        <h1 className="title">Choose Services</h1>
        <div className="button-group">
          {lists?.map(list => (
            <button
              key={list}
              onClick={() => handleSelectList(list)}
              className={`button ${
                selectedList?.name === list ? 'selected' : 'default'
              }`}
            >
              {list}
            </button>
          ))}
        </div>
        <div className="add-email">
          <input
            type="text"
            placeholder="Add new email or search"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <button onClick={handleAddEmail} className="button edit">Add Email</button>
        </div>
        {selectedList ? (
          <div className="list-container" style={{ marginTop: '20px' }}>
            <h2 className="list-title">Up-to-Date Distribution List for {selectedList.name}</h2>
            <p style={{ fontStyle: 'italic', fontSize: '14px'}}>Sorted alphabetically</p>
            <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '10px', marginTop:'10px' }}>
              <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
                {filteredEmails?.map(email => (
                  <li key={email} className="list-item" style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    {emailToEdit === email ? (
                      <>
                        <input
                          type="text"
                          value={editedEmail}
                          onChange={(e) => setEditedEmail(e.target.value)}
                        />
                        <button onClick={handleCancelEdit} className="button cancel">Cancel</button>
                        <button onClick={handleConfirmEdit} className="button confirm">Confirm</button>
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1 }}>{email}</span>
                        <button onClick={() => handleEditEmailStart(email)} className="button edit">Edit</button>
                        <button onClick={() => handleDeleteClick(email)} className="button delete">Delete</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={exportToCSV} className="export-button">Export to CSV</button>
              <label htmlFor="csv-import" className="export-button" style={{ cursor: 'pointer', display: 'inline-block' }}>
                Import from CSV
              </label>
              <input
                id="csv-import"
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        ) : (
          <p style={{marginTop:'20px'}}>Need to select a distribution list to view details</p>
        )}
      </div>
      <ConfirmModal
        isOpen={showModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message={
          <div>
            Are you sure you want to delete the email: <b>{emailToDelete}</b>? 
          <br />
          <i>
            <span style={{ fontSize: '12px' }}>
              It will be stored in the database for 30 days before being permanently deleted.
            </span>
          </i>
        </div>
        }
      />
    </MainLayout>
  );
}
  

export default DistributionListApp;
